/**
 * Secure Logger Utility
 * 
 * Prevents sensitive data from being logged in production.
 * Only logs in development mode.
 * 
 * Usage:
 *   import { devLog, devError, devWarn } from '@/utils/secureLogger';
 *   devLog('Safe message', { data }); // Only logs in dev
 */

const isDevelopment = import.meta.env.DEV;

/**
 * Sanitizes data by removing sensitive fields before logging
 */
const sanitizeForLogging = (data: any): any => {
  if (!data || typeof data !== 'object') return data;

  const sensitiveFields = [
    'password',
    'token',
    'secret',
    'apiKey',
    'api_key',
    'authorization',
    'creditCard',
    'credit_card',
    'ssn',
    'bankAccount',
    'bank_account'
  ];

  if (Array.isArray(data)) {
    return data.map(item => sanitizeForLogging(item));
  }

  const sanitized: any = {};
  for (const [key, value] of Object.entries(data)) {
    const lowerKey = key.toLowerCase();
    if (sensitiveFields.some(field => lowerKey.includes(field))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeForLogging(value);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
};

/**
 * Development-only console.log
 */
export const devLog = (...args: any[]) => {
  if (isDevelopment) {
    const sanitizedArgs = args.map(arg => 
      typeof arg === 'object' ? sanitizeForLogging(arg) : arg
    );
    console.log(...sanitizedArgs);
  }
};

/**
 * Development-only console.error
 */
export const devError = (...args: any[]) => {
  if (isDevelopment) {
    const sanitizedArgs = args.map(arg => 
      typeof arg === 'object' ? sanitizeForLogging(arg) : arg
    );
    console.error(...sanitizedArgs);
  }
};

/**
 * Development-only console.warn
 */
export const devWarn = (...args: any[]) => {
  if (isDevelopment) {
    const sanitizedArgs = args.map(arg => 
      typeof arg === 'object' ? sanitizeForLogging(arg) : arg
    );
    console.warn(...sanitizedArgs);
  }
};

/**
 * Always logs errors (even in production) but sanitizes sensitive data
 */
export const logError = (message: string, error: any) => {
  const sanitizedError = sanitizeForLogging(error);
  console.error(message, sanitizedError);
};

/**
 * Logs only in production for critical monitoring
 */
export const prodLog = (...args: any[]) => {
  if (!isDevelopment) {
    const sanitizedArgs = args.map(arg => 
      typeof arg === 'object' ? sanitizeForLogging(arg) : arg
    );
    console.log(...sanitizedArgs);
  }
};
